import React, { useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import {
  HelpCircleIcon,
  TrendingUpIcon,
  TrendingDownIcon,
} from "../../../commonUI/Icons/SvgIcon";

const UserActivityTable = ({ platforms = [] }) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });

  const headerTooltips = {
    activeUsers: "Distinct active users in the selected period. Counted separately on each platform.",
    sessions: "Total sessions across all platforms in the selected period.",
    engagementRate: "Percentage of active users who had at least one session.",
  };

  const platformNames = ["Website", "iOS app", "Android app"];
  const defaultPlatforms = [
    {
      name: "Website",
      activeUsers: 7176,
      sessions: 18908,
      engagementRate: "76.9%",
      activeUsersChange: -4.1,
      sessionsChange: -5.3,
      engagementRateChange: -0.4,
    },
    {
      name: "iOS app",
      activeUsers: 5677,
      sessions: 22648,
      engagementRate: "71.9%",
      activeUsersChange: -0.7,
      sessionsChange: 3.2,
      engagementRateChange: -0.1,
    },
    {
      name: "Android app",
      activeUsers: 3351,
      sessions: 11662,
      engagementRate: "72.2%",
      activeUsersChange: -1.8,
      sessionsChange: -4.8,
      engagementRateChange: -0.2,
    },
  ];

  const transformPlatforms = (data) => {
    return data.slice(0, 3).map((item, idx) => {
      const trendDefaults = [
        { activeUsersChange: -4.1, sessionsChange: -5.3, engagementRateChange: -0.4 },
        { activeUsersChange: -0.7, sessionsChange: 3.2, engagementRateChange: -0.1 },
        { activeUsersChange: -1.8, sessionsChange: -4.8, engagementRateChange: -0.2 },
      ];
      const defaultTrend = trendDefaults[idx] || {};

      return {
        name: platformNames[idx] || `Platform ${idx + 1}`,
        activeUsers: item.activeUsers || 0,
        sessions: item.sessions || 0,
        engagementRate: item.engagementRate
          ? typeof item.engagementRate === "number"
            ? `${(item.engagementRate * 100).toFixed(1)}%`
            : item.engagementRate
          : "0%",
        activeUsersChange:
          item.activeUsersChange !== undefined ? item.activeUsersChange : defaultTrend.activeUsersChange,
        sessionsChange:
          item.sessionsChange !== undefined ? item.sessionsChange : defaultTrend.sessionsChange,
        engagementRateChange:
          item.engagementRateChange !== undefined ? item.engagementRateChange : defaultTrend.engagementRateChange,
      };
    });
  };

  const data = platforms?.length > 0 ? transformPlatforms(platforms) : defaultPlatforms;

  const sortedData = [...data].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    if (typeof aValue === "number" && typeof bValue === "number") {
      return sortConfig.direction === "asc" ? aValue - bValue : bValue - aValue;
    }
    if (typeof aValue === "string" && typeof bValue === "string") {
      return sortConfig.direction === "asc" ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
    }
    return 0;
  });

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const getTrendColor = (change) =>
    change !== undefined && change !== null && change >= 0 ? "#059669" : "#ef4444";
  const getTrendArrow = (change) =>
    change !== undefined && change !== null && change >= 0 ? "↑" : "↓";
  const formatTrend = (change) => {
    if (change === undefined || change === null) return "";
    return `${getTrendArrow(change)} ${change >= 0 ? "+" : ""}${change.toFixed(1)}${typeof change === "number" && Math.abs(change) < 10 ? "%" : "pp"}`;
  };

  return (
    <div className="user-activity-table">
      <div className="user-activity-table__header">
        <h3 className="user-activity-table__title">User activity</h3>
      </div>
      <hr className="user-activity-table__divider" />
      <div className="user-activity-table__wrapper">
        <table className="user-activity-table__table">
          <thead>
            <tr className="user-activity-table__head-row">
              <th onClick={() => handleSort("name")} className="user-activity-table__head-cell">
                Platform{" "}
                {sortConfig.key === "name" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
              </th>
              <th
                onClick={() => handleSort("activeUsers")}
                className="user-activity-table__head-cell user-activity-table__head-cell--center"
              >
                Active users{" "}
                <Tooltip
                  title={headerTooltips?.activeUsers}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#fff',
                        color: '#000',
                        fontSize: '14px',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                      },
                    },
                  }}
                >
                  <span style={{ display: "inline-block", cursor: "help" }}>
                    <HelpCircleIcon width="14" height="14" />
                  </span>
                </Tooltip>{" "}
                {sortConfig.key === "activeUsers" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↓"}
              </th>
              <th
                onClick={() => handleSort("sessions")}
                className="user-activity-table__head-cell user-activity-table__head-cell--center"
              >
                Sessions{" "}
                <Tooltip
                  title={headerTooltips.sessions}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#fff',
                        color: '#000',
                        fontSize: '14px',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                      },
                    },
                  }}
                >
                  <span style={{ display: "inline-block", cursor: "help" }}>
                    <HelpCircleIcon width="14" height="14" />
                  </span>
                </Tooltip>{" "}
                {sortConfig.key === "sessions" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
              </th>
              <th
                onClick={() => handleSort("engagementRate")}
                className="user-activity-table__head-cell user-activity-table__head-cell--right"
              >
                Engagement rate{" "}
                <Tooltip
                  title={headerTooltips.engagementRate}
                  slotProps={{
                    tooltip: {
                      sx: {
                        backgroundColor: '#fff',
                        color: '#000',
                        fontSize: '14px',
                        boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                      },
                    },
                  }}
                >
                  <span style={{ display: "inline-block", cursor: "help" }}>
                    <HelpCircleIcon width="14" height="14" />
                  </span>
                </Tooltip>{" "}
                {sortConfig.key === "engagementRate" ? (sortConfig.direction === "asc" ? "↑" : "↓") : "↕"}
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedData.map((platform, idx) => (
              <tr key={idx} className="user-activity-table__body-row">
                <td className="user-activity-table__body-cell user-activity-table__body-cell--header">
                  {platform.name}
                </td>
                <td className="user-activity-table__body-cell user-activity-table__body-cell--center">
                  <div className="user-activity-table__metric-value">
                    {typeof platform.activeUsers === "number"
                      ? platform.activeUsers.toLocaleString()
                      : platform.activeUsers}
                  </div>
                  <div
                    className={`user-activity-table__trend ${
                      getTrendColor(platform.activeUsersChange) === "#059669"
                        ? "user-activity-table__trend--positive"
                        : "user-activity-table__trend--negative"
                    }`}
                  >
                    {getTrendColor(platform.activeUsersChange) === "#059669" ? (
                      <TrendingUpIcon width="14" height="14" />
                    ) : (
                      <TrendingDownIcon width="14" height="14" />
                    )}
                    {formatTrend(platform.activeUsersChange)}
                  </div>
                </td>
                <td className="user-activity-table__body-cell user-activity-table__body-cell--center">
                  <div className="user-activity-table__metric-value">
                    {typeof platform.sessions === "number"
                      ? platform.sessions.toLocaleString()
                      : platform.sessions}
                  </div>
                  <div
                    className={`user-activity-table__trend ${
                      getTrendColor(platform.sessionsChange) === "#059669"
                        ? "user-activity-table__trend--positive"
                        : "user-activity-table__trend--negative"
                    }`}
                  >
                    {getTrendColor(platform.sessionsChange) === "#059669" ? (
                      <TrendingUpIcon width="14" height="14" />
                    ) : (
                      <TrendingDownIcon width="14" height="14" />
                    )}
                    {formatTrend(platform.sessionsChange)}
                  </div>
                </td>
                <td className="user-activity-table__body-cell user-activity-table__body-cell--right">
                  <div className="user-activity-table__metric-value">
                    {platform.engagementRate}
                  </div>
                  <div
                    className={`user-activity-table__trend ${
                      getTrendColor(platform.engagementRateChange) === "#059669"
                        ? "user-activity-table__trend--positive"
                        : "user-activity-table__trend--negative"
                    }`}
                  >
                    {getTrendColor(platform.engagementRateChange) === "#059669" ? (
                      <TrendingUpIcon width="14" height="14" />
                    ) : (
                      <TrendingDownIcon width="14" height="14" />
                    )}
                    {formatTrend(platform.engagementRateChange)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserActivityTable;
